import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Shield, 
  Map, 
  FileText, 
  CheckCircle, 
  ArrowRight, 
  AlertTriangle,
  Clock,
  Maximize2,
  Minimize2,
  X
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../components/ui/button';
import LanguageSelector from '../components/LanguageSelector';
import { FlightMap } from '../components/map/FlightMap';
import HeroMapTeaser from '../components/map/HeroMapTeaser';
import ErrorBoundary from '../components/ErrorBoundary';
import CountryComparator from '../components/CountryComparator';
import { HreflangTags, useSyncLanguageFromURL } from '../components/HreflangTags';
import SeoMeta from '../components/SeoMeta';
import BlogTeaser from '../components/BlogTeaser';
import SummerPromoBanner, { formatEUR } from '../components/SummerPromoBanner';
import { LinkedInLink } from '../components/LinkedInLink';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent } from '../lib/analytics';
import { usePublicPromo, promoPriceFor } from '../hooks/usePublicPromo';
import { LANDING_SEO, ORGANIZATION_JSONLD, buildBreadcrumbJsonLd } from '../data/seoContent';
import { COUNTRY_SLUGS, getCountryContent } from '../data/countriesContent';
import { buildLocalizedPath, DEFAULT_LANGUAGE } from '../lib/i18nRouting';

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  useSyncLanguageFromURL();
  const lang = i18n.language || DEFAULT_LANGUAGE;
  const seo = LANDING_SEO[lang] || LANDING_SEO.fr;
  const [landingMapWide, setLandingMapWide] = useState(false);
  const { promo } = usePublicPromo();
  const soloPromo = promoPriceFor(promo, 'solo', 19);
  const annualPromo = promoPriceFor(promo, 'solo_annual', 190);

  /**
   * Fired when an anonymous visitor draws a polygon on the landing map.
   *
   * We (a) persist the geometry so we can restore it in the New Mission
   * form after signup, (b) surface a friendly toast with a signup CTA
   * ("compliance analysis is behind an account"), and (c) log a strong
   * product-analytics signal — this is the highest-intent action a
   * non-user can perform on the site.
   */
  const handleLandingPolygonDrawn = (geometry) => {
    if (!geometry) return;
    try {
      sessionStorage.setItem(
        'dronemaps_pending_mission_polygon',
        JSON.stringify({ geometry, drawnAt: Date.now() }),
      );
    } catch { /* private mode — silently no-op */ }
    trackEvent('cta_clicked', { name: 'landing_polygon_drawn' });
    toast.success(
      t(
        'landing.map.polygonDrawnCta',
        "Belle zone ! Créez un compte gratuit pour lancer l'analyse de conformité →",
      ),
      {
        duration: 8000,
        action: {
          label: t('landing.hero.cta', 'Créer un compte'),
          onClick: () => { window.location.href = '/register'; },
        },
      },
    );
  };

  const landingJsonLd = [
    ORGANIZATION_JSONLD,
    buildBreadcrumbJsonLd([{ name: 'Drone Maps', url: 'https://drone-maps.eu' }]),
  ];

  const features = [
    {
      icon: Map,
      title: t('landing.features.map.title'),
      description: t('landing.features.map.description')
    },
    {
      icon: Shield,
      title: t('landing.features.regulation.title'),
      description: t('landing.features.regulation.description')
    },
    {
      icon: FileText,
      title: t('landing.features.pdf.title'),
      description: t('landing.features.pdf.description')
    },
    {
      icon: Clock,
      title: t('landing.features.fast.title'),
      description: t('landing.features.fast.description')
    }
  ];

  const steps = [
    { number: '01', title: t('landing.howItWorks.step1.title'), description: t('landing.howItWorks.step1.description') },
    { number: '02', title: t('landing.howItWorks.step2.title'), description: t('landing.howItWorks.step2.description') },
    { number: '03', title: t('landing.howItWorks.step3.title'), description: t('landing.howItWorks.step3.description') },
    { number: '04', title: t('landing.howItWorks.step4.title'), description: t('landing.howItWorks.step4.description') },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <SeoMeta
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        rawTitle
        jsonLd={landingJsonLd}
      />
      <HreflangTags />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#1b2236] border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
              <img src="/logo-landscape.png" alt="Drone Maps" className="h-14 md:h-16 w-auto" />
            </Link>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-300 hover:text-white text-sm font-medium transition-colors">{t('nav.features')}</a>
              <a href="#pricing" className="text-slate-300 hover:text-white text-sm font-medium transition-colors">{t('nav.pricing')}</a>
              <a href="#how-it-works" className="text-slate-300 hover:text-white text-sm font-medium transition-colors">{t('nav.howItWorks')}</a>
              <Link to="/blog" className="text-slate-300 hover:text-white text-sm font-medium transition-colors" data-testid="blog-nav-link">{t('nav.blog')}</Link>
            </nav>
            <div className="flex items-center gap-3">
              <LanguageSelector variant="compact" />
              <Link to="/login">
                <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800" data-testid="login-nav-btn">
                  {t('nav.login')}
                </Button>
              </Link>
              <Link to="/register" onClick={() => trackEvent('cta_clicked', { name: 'header_register' })}>
                <Button className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="register-nav-btn">
                  {t('landing.hero.cta')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Sticky summer promo banner — self-hides if no active promo. */}
      <SummerPromoBanner to="#pricing" />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-[#1b2236] pt-16">
        <div 
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1647563920660-b21ae84b0070?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxhZXJpYWwlMjB2aWV3JTIwZnJhbmNlJTIwbGFuZHNjYXBlJTIwZmllbGRzJTIwY2l0eXxlbnwwfHx8fDE3NzEyNDgzMTd8MA&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-400 text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                {t('landing.hero.badge')}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-[Barlow] tracking-tight leading-tight mb-6">
                {t('landing.hero.title')}
              </h1>
              <p className="text-lg text-slate-300 mb-8 max-w-xl">
                {t('landing.hero.subtitle')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register" onClick={() => trackEvent('cta_clicked', { name: 'hero_register' })}>
                  <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8" data-testid="hero-cta-btn">
                    {t('landing.hero.cta')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="#try-map" onClick={() => trackEvent('cta_clicked', { name: 'hero_demo' })}>
                  <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="hero-demo-btn">
                    {t('landing.hero.demo')}
                  </Button>
                </a>
              </div>
              <div className="flex items-center gap-6 mt-8 text-sm text-slate-400">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>{t('landing.hero.freeMissions')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>{t('landing.hero.noCard')}</span>
                </div>
              </div>
            </div>
            <div className="hidden lg:block">
              <ErrorBoundary name="HeroMapTeaser">
                <HeroMapTeaser scrollTargetId="try-map" />
              </ErrorBoundary>
            </div>
          </div>
        </div>
      </section>

      {/* (LandingMapPreview removed — replaced by the full interactive FlightMap below) */}

      {/* European Coverage Section — replaces the old "Explorez la carte réglementaire" hero map */}
      <section id="try-map" className="py-24 bg-white" data-testid="landing-coverage-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-xs font-semibold">
              <span>🌍</span>
              <span>{t('landing.coverage.badge', '31 pays · Sources officielles')}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-[Barlow] mb-3">
              {t('landing.exploreMap.title', 'Explorez la carte réglementaire')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-2">
              {t('landing.exploreMap.subtitle', 'Activez les couches qui vous intéressent depuis le menu « Couches » pour explorer les zones réglementées de chaque pays.')}
            </p>
            <p className="text-sm text-slate-500 italic">
              {t('landing.coverage.tryNow', '👉 Naviguez, zoomez, activez/désactivez les couches — aucune inscription requise')}
            </p>
          </div>

          {/* Interactive demo map — same engine as logged-in app, read-only */}
          <div className="flex justify-end mb-2">
            <button
              onClick={() => setLandingMapWide(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-sky-700 bg-sky-50 hover:bg-sky-100 border border-sky-200 rounded-lg transition-colors"
              data-testid="landing-map-wide-toggle"
            >
              <Maximize2 className="w-4 h-4" />
              {t('landing.coverage.wideMode', 'Mode large')}
            </button>
          </div>
          <ErrorBoundary name="LandingInteractiveMap">
            <div
              className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm"
              style={{ height: 'min(80vh, 760px)', minHeight: '560px' }}
              data-testid="landing-interactive-map"
            >
              <FlightMap
                showZones={true}
                editable={true}
                demoMode={true}
                onPolygonDrawn={handleLandingPolygonDrawn}
                height="100%"
              />
            </div>
          </ErrorBoundary>

          {/* Wide-mode overlay for the landing map */}
          {landingMapWide && (
            <div className="fixed inset-0 z-[70] bg-white flex flex-col" data-testid="landing-map-wide-overlay">
              <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-[#1b2236] text-white">
                <div className="flex items-center gap-3">
                  <Map className="w-5 h-5 text-sky-400" />
                  <span className="font-semibold font-[Barlow]">
                    {t('landing.coverage.demoTitle', 'Démonstration carte interactive — Drone Maps')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Link to="/register" onClick={() => trackEvent('cta_clicked', { name: 'wide_map_register' })}>
                    <Button className="bg-sky-500 hover:bg-sky-600 text-white text-sm">
                      {t('landing.hero.cta', 'Commencer gratuitement')}
                    </Button>
                  </Link>
                  <button
                    onClick={() => setLandingMapWide(false)}
                    className="p-2 rounded hover:bg-slate-700 text-slate-200"
                    data-testid="landing-map-wide-close"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="flex-1">
                <FlightMap
                  showZones={true}
                  editable={true}
                  demoMode={true}
                  onPolygonDrawn={handleLandingPolygonDrawn}
                  height="100%"
                />
              </div>
            </div>
          )}

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div className="p-4 rounded-lg border border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-900 mb-1">🛡️ {t('landing.coverage.benefits.sources.title', 'Sources légales')}</div>
              <p className="text-slate-600 text-xs">{t('landing.coverage.benefits.sources.desc', 'DGAC, DFS, NGI Belgique, DAC Luxembourg, LVNL, BAZL, Austro Control, AIM ČR, LPS SR, Trafikstyrelsen, IAA, Transpordiamet, EANS, DCA Cyprus, ENAC, AESA, ULC, ANAC, Samgöngustofa.')}</p>
            </div>
            <div className="p-4 rounded-lg border border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-900 mb-1">⚡ {t('landing.coverage.benefits.realtime.title', 'Mises à jour temps réel')}</div>
              <p className="text-slate-600 text-xs">{t('landing.coverage.benefits.realtime.desc', 'Les couches WMS sont consommées en direct depuis les serveurs officiels. Aucune donnée mise en cache.')}</p>
            </div>
            <div className="p-4 rounded-lg border border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-900 mb-1">📍 {t('landing.coverage.benefits.autodetect.title', 'Détection automatique')}</div>
              <p className="text-slate-600 text-xs">{t('landing.coverage.benefits.autodetect.desc', 'Chaque mission identifie automatiquement le pays et applique la réglementation EU 2019/947 + spécificités locales.')}</p>
            </div>
            <div className="p-4 rounded-lg border border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-900 mb-1">🤝 {t('landing.coverage.benefits.authorities.title', 'Contacts autorités')}</div>
              <p className="text-slate-600 text-xs">{t('landing.coverage.benefits.authorities.desc', 'Liste précise des organismes à contacter avec liens directs vers les portails officiels d\'autorisation.')}</p>
            </div>
          </div>
        </div>
      </section>


      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-[Barlow] mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-50 rounded-lg border border-slate-200 hover:border-sky-300 hover:shadow-lg transition-all duration-200"
              >
                <div className="w-12 h-12 bg-sky-500/10 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-sky-500" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2 font-[Barlow]">{feature.title}</h3>
                <p className="text-slate-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-[Barlow] mb-4">
              {t('landing.howItWorks.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('landing.howItWorks.subtitle')}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-bold text-slate-200 font-[Barlow] mb-4">{step.number}</div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2 font-[Barlow]">{step.title}</h3>
                <p className="text-slate-600 text-sm">{step.description}</p>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-full w-full">
                    <ArrowRight className="w-6 h-6 text-slate-300 -ml-3" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Country Regulation Hub (SEO long-tail) */}
      <section className="py-16 bg-slate-50 border-t border-slate-200" data-testid="landing-country-hub">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-[Barlow] mb-3">
              {t('landing.countries.title', 'Réglementation drone par pays')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('landing.countries.subtitle', 'Guides complets de la réglementation drone pour les 31 pays couverts par Drone Maps.')}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {COUNTRY_SLUGS.map((slug) => {
              const c = getCountryContent(slug, lang);
              if (!c) return null;
              const isOverseas = slug === 'france-outre-mer';
              return (
                <Link
                  key={slug}
                  to={buildLocalizedPath(`/regulations/${slug}`, lang)}
                  className={`group flex items-center justify-between rounded-lg px-4 py-3 transition-all ${
                    isOverseas
                      ? 'bg-sky-50 border-2 border-sky-300 hover:border-sky-500 hover:shadow-md'
                      : 'bg-white border border-slate-200 hover:border-sky-400 hover:shadow-md'
                  }`}
                  data-testid={`landing-country-link-${slug}`}
                >
                  <div className="flex flex-col">
                    <span className={`font-semibold ${isOverseas ? 'text-sky-700' : 'text-slate-800 group-hover:text-sky-600'}`}>
                      {c.name}
                    </span>
                    {isOverseas && (
                      <span className="text-[10px] uppercase tracking-wider text-sky-500 font-semibold mt-0.5">
                        {t('landing.countries.overseasBadge', '10 territoires • 592 zones')}
                      </span>
                    )}
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-sky-500 group-hover:translate-x-0.5 transition-all" />
                </Link>
              );
            })}
          </div>

          {/* DOM-TOM info badge */}
          <div className="mt-8 flex items-center justify-center gap-2 text-sm text-slate-600" data-testid="landing-overseas-badge">
            <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1.5 rounded-full text-xs font-medium">
              ✓ {t('landing.countries.overseasIncluded', 'DOM-TOM inclus dans la couverture officielle')}
            </span>
          </div>
        </div>
      </section>

      {/* Cross-country regulatory comparator — standalone mode so both
          sides are pickable. Placed right after the country hub so the
          visitor can dive into any pairwise comparison in one click.
          Big SEO win: 32×31 = 992 pairwise combinations discoverable
          from the landing page. */}
      <CountryComparator lang={lang} standalone={true} />

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-[Barlow] mb-4">
              {t('landing.pricing.title')}
            </h2>
            <p className="text-lg text-slate-600">
              {t('landing.pricing.subtitle')}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Free Plan */}
            <div className="pricing-card">
              <h3 className="text-xl font-bold text-slate-900 mb-2 font-[Barlow]">{t('landing.pricing.free.name')}</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-slate-900">{t('landing.pricing.free.price')}</span>
                <span className="text-slate-600">{t('landing.pricing.perMonth')}</span>
              </div>
              <ul className="space-y-3 mb-8 text-left">
                <li className="flex items-center gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  {t('landing.pricing.free.features.missions')}
                </li>
                <li className="flex items-center gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  {t('landing.pricing.free.features.pdf')}
                </li>
                <li className="flex items-center gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  {t('landing.pricing.free.features.support')}
                </li>
                <li className="flex items-center gap-3 text-sm text-slate-400">
                  <AlertTriangle className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  {t('landing.pricing.free.features.limit')}
                </li>
              </ul>
              <Link to="/register" onClick={() => trackEvent('cta_clicked', { name: 'pricing_free' })} className="block">
                <Button variant="outline" className="w-full" data-testid="free-plan-btn">
                  {t('landing.pricing.free.cta')}
                </Button>
              </Link>
            </div>

            {/* Solo Monthly Plan */}
            <div className="pricing-card featured relative" data-testid="solo-plan-card">
              {soloPromo.hasPromo && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-rose-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-md whitespace-nowrap uppercase">
                  {t('landing.promo.cardBadge')}
                </div>
              )}
              <h3 className="text-xl font-bold text-slate-900 mb-2 font-[Barlow]">{t('landing.pricing.solo.name')}</h3>
              <div className="mb-2">
                {soloPromo.hasPromo && (
                  <span className="text-2xl font-bold text-slate-400 line-through mr-2">
                    {formatEUR(19, lang)}
                  </span>
                )}
                <span className={`text-4xl font-bold ${soloPromo.hasPromo ? 'text-orange-600' : 'text-slate-900'}`}>
                  {soloPromo.hasPromo ? formatEUR(soloPromo.finalPrice, lang) : t('landing.pricing.solo.price')}
                </span>
                <span className="text-slate-600">{t('landing.pricing.perMonth')}</span>
              </div>
              {soloPromo.hasPromo && (
                <p className="mb-4 text-xs text-orange-700 font-medium">
                  {t('landing.promo.renewalMonthlyNote', { code: 'SUMMER', base: formatEUR(19, lang) })}
                </p>
              )}
              <ul className="space-y-3 mb-8 text-left">
                <li className="flex items-center gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  {t('landing.pricing.solo.features.missions')}
                </li>
                <li className="flex items-center gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  {t('landing.pricing.solo.features.pdf')}
                </li>
                <li className="flex items-center gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  {t('landing.pricing.solo.features.history')}
                </li>
                <li className="flex items-center gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  {t('landing.pricing.solo.features.support')}
                </li>
              </ul>
              <div className="mb-4 p-2.5 rounded-lg border bg-emerald-50 border-emerald-200 flex items-start gap-2" data-testid="solo-plan-no-autorenew">
                <ShieldCheck className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-emerald-800 leading-snug">
                  <span className="font-semibold">{t('landing.pricing.noAutoRenew.title', 'Sans engagement · Aucun prélèvement automatique.')}</span>
                  <br />
                  {t('landing.pricing.noAutoRenew.body', "Rappel email avant l'échéance, vous décidez librement de renouveler.")}
                </div>
              </div>
              <Link
                to={`/register?plan=solo${soloPromo.hasPromo ? '&promo=SUMMER' : ''}`}
                onClick={() => trackEvent('cta_clicked', { name: 'pricing_solo_monthly', has_promo: soloPromo.hasPromo })}
                className="block"
              >
                <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white" data-testid="solo-plan-btn">
                  {t('landing.pricing.solo.cta')}
                </Button>
              </Link>
            </div>

            {/* Solo Annual Plan - Best value */}
            <div className="pricing-card relative border-2 border-emerald-500 bg-gradient-to-b from-emerald-50/50 to-white" data-testid="annual-plan-card">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-md whitespace-nowrap">
                {annualPromo.hasPromo ? `🔥 ${t('landing.promo.annualBadgeDiscount')}` : `⭐ ${t('landing.pricing.annual.badge', '2 mois offerts')}`}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2 font-[Barlow]">{t('landing.pricing.annual.name', 'Solo Annuel')}</h3>
              <div className="mb-2">
                {annualPromo.hasPromo && (
                  <span className="text-2xl font-bold text-slate-400 line-through mr-2">
                    {formatEUR(190, lang)}
                  </span>
                )}
                <span className={`text-4xl font-bold ${annualPromo.hasPromo ? 'text-orange-600' : 'text-slate-900'}`}>
                  {annualPromo.hasPromo ? formatEUR(annualPromo.finalPrice, lang) : t('landing.pricing.annual.price', '190€')}
                </span>
                <span className="text-slate-600">{t('landing.pricing.perYear', '/an')}</span>
              </div>
              <div className="mb-6 text-xs text-slate-500">
                {annualPromo.hasPromo ? (
                  <span className="text-orange-700 font-semibold">
                    {t('landing.promo.renewalAnnualNote', {
                      perMonth: formatEUR(annualPromo.finalPrice / 12, lang),
                      code: 'SUMMER',
                    })}
                  </span>
                ) : (
                  <>
                    <span className="line-through">{formatEUR(228, lang)}</span>
                    <span className="ml-2 text-emerald-600 font-semibold">{t('landing.pricing.annual.equivalent', 'soit 15,83€/mois')}</span>
                  </>
                )}
              </div>
              <ul className="space-y-3 mb-8 text-left">
                <li className="flex items-center gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  {t('landing.pricing.solo.features.missions')}
                </li>
                <li className="flex items-center gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  {t('landing.pricing.solo.features.pdf')}
                </li>
                <li className="flex items-center gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  {t('landing.pricing.solo.features.history')}
                </li>
                <li className="flex items-center gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  {t('landing.pricing.solo.features.support')}
                </li>
                <li className="flex items-center gap-3 text-sm text-emerald-700 font-semibold">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  {annualPromo.hasPromo
                    ? t('landing.promo.savingsVsMonthly', { amount: formatEUR(19 * 12 - annualPromo.finalPrice, lang) })
                    : t('landing.pricing.annual.savings', '38€ d\'économies / an')}
                </li>
              </ul>
              <div className="mb-4 p-2.5 rounded-lg border bg-emerald-50 border-emerald-200 flex items-start gap-2" data-testid="solo-annual-plan-no-autorenew">
                <ShieldCheck className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-emerald-800 leading-snug">
                  <span className="font-semibold">{t('landing.pricing.noAutoRenew.title', 'Sans engagement · Aucun prélèvement automatique.')}</span>
                  <br />
                  {t('landing.pricing.noAutoRenew.body', "Rappel email avant l'échéance, vous décidez librement de renouveler.")}
                </div>
              </div>
              <Link
                to={`/register?plan=solo_annual${annualPromo.hasPromo ? '&promo=SUMMER' : ''}`}
                onClick={() => trackEvent('cta_clicked', { name: 'pricing_solo_annual', has_promo: annualPromo.hasPromo })}
                className="block"
              >
                <Button className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-md" data-testid="annual-plan-btn">
                  {annualPromo.hasPromo ? t('landing.promo.annualCta') : t('landing.pricing.annual.cta', 'Économiser 38€')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Blog teaser — surfaces the 3 most recent articles to drive
          organic traffic to /blog and reinforce expertise before the
          final CTA. Self-unmounts if the blog is empty. */}
      <BlogTeaser />

      {/* CTA Section */}
      <section className="py-24 bg-[#1b2236]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white font-[Barlow] mb-6">
            {t('landing.cta.title')}
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            {t('landing.cta.subtitle')}
          </p>
          <Link to="/register" onClick={() => trackEvent('cta_clicked', { name: 'final_cta_register' })}>
            <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8" data-testid="final-cta-btn">
              {t('landing.cta.button')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#141931] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img src="/logo-landscape.png" alt="Drone Maps" className="h-8 w-auto" />
            <div className="flex items-center gap-6 flex-wrap justify-center">
              {lang === 'fr' && (
                <>
                  <Link to="/preparation-vol-drone" className="text-slate-400 hover:text-white text-sm transition-colors" data-testid="landing-footer-preparation-vol">
                    Préparation vol drone
                  </Link>
                  <Link to="/mission-drone" className="text-slate-400 hover:text-white text-sm transition-colors" data-testid="landing-footer-mission-drone">
                    Mission drone
                  </Link>
                </>
              )}
              <Link to="/faq" className="text-slate-400 hover:text-white text-sm transition-colors">
                {t('nav.faq')}
              </Link>
              <Link to="/blog" className="text-slate-400 hover:text-white text-sm transition-colors">
                {t('nav.blog')}
              </Link>
              <Link to="/contact" className="text-slate-400 hover:text-white text-sm transition-colors" data-testid="landing-footer-contact">
                {t('sidebar.contact', 'Contact')}
              </Link>
              <LinkedInLink />
              <p className="text-slate-400 text-sm">
                {t('footer.copyright', { year: new Date().getFullYear() })}
              </p>
            </div>
          </div>
          
          {/* Liens légaux */}
          <div className="mt-8 pt-6 border-t border-slate-800 flex flex-wrap justify-center gap-4 text-xs">
            <Link to="/mentions-legales" className="text-slate-500 hover:text-slate-300 transition-colors">
              Mentions légales
            </Link>
            <Link to="/confidentialite" className="text-slate-500 hover:text-slate-300 transition-colors">
              Politique de confidentialité
            </Link>
            <Link to="/cookies" className="text-slate-500 hover:text-slate-300 transition-colors">
              Cookies
            </Link>
            <Link to="/cgu" className="text-slate-500 hover:text-slate-300 transition-colors">
              CGU
            </Link>
            <Link to="/cgv" className="text-slate-500 hover:text-slate-300 transition-colors">
              CGV
            </Link>
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-xs text-slate-500">
              {t('footer.disclaimer')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
